import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { formatRelativeDate, type Client, type User } from '@oneup/shared';
import { clientsApi, usersApi } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  ManagementCardGrid,
  ManagementFooterSummary,
  ManagementHeader,
  ManagementLoadingState,
  ManagementPage,
  ManagementTable,
  type ManagementActiveFilter,
} from '@/components/management';
import { MANAGEMENT_PAGES, usePersistentManagementView } from '@/platform/management-pages';
import { useUiStore } from '@/stores/ui';
import { Avatar, LoadError, MutedDash } from './ClientsView';
import { ClientDrawer, UserDrawer } from './entity-drawers';
import { TaskDrawer } from './TaskDrawer';

type TypeFilter = 'Client' | 'One Up Webmaster' | 'All';

function parseTypeFilter(value: string | null): TypeFilter {
  return value === 'One Up Webmaster' || value === 'All' ? value : 'Client';
}

export function UsersView() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userId } = useParams<{ userId: string }>();
  const type = parseTypeFilter(searchParams.get('type'));
  const search = searchParams.get('search') ?? '';
  const [viewMode, setViewMode] = usePersistentManagementView(
    'contacts',
    searchParams,
    setSearchParams,
  );
  const openCreateDrawer = useUiStore((state) => state.openCreateDrawer);
  const [nestedTaskId, setNestedTaskId] = useState<string>();
  const [nestedClientId, setNestedClientId] = useState<string>();

  const usersQuery = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => usersApi.list(),
  });
  const clientsQuery = useQuery({
    queryKey: ['clients', 'all'],
    queryFn: () => clientsApi.list(),
  });

  const users = usersQuery.data?.items ?? [];
  const clientMap = useMemo(
    () => new Map((clientsQuery.data?.items ?? []).map((client: Client) => [client.id, client])),
    [clientsQuery.data?.items],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((user) => {
      if (type !== 'All' && !(user.userTypes ?? []).includes(type)) return false;
      if (!q) return true;
      return (
        user.fullName.toLowerCase().includes(q) ||
        (user.email ?? '').toLowerCase().includes(q)
      );
    });
  }, [search, type, users]);

  const setType = useCallback(
    (value: TypeFilter) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (value === 'Client') params.delete('type');
          else params.set('type', value);
          return params;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const setSearch = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (value) params.set('search', value);
          else params.delete('search');
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  if (usersQuery.isLoading) return <ManagementLoadingState metricCount={3} />;
  if (usersQuery.error) return <LoadError title="Couldn't load contacts" error={usersQuery.error} />;

  const page = MANAGEMENT_PAGES.contacts;
  const activeFilters: ManagementActiveFilter[] = [];
  if (type !== 'Client') {
    activeFilters.push({ id: 'type', label: 'Type', value: type, onClear: () => setType('Client') });
  }
  if (search) {
    activeFilters.push({ id: 'search', label: 'Search', value: `"${search}"`, onClear: () => setSearch('') });
  }

  return (
    <ManagementPage>
      <ManagementHeader
        title={page.title}
        description={page.description}
        source={page.source}
        search={{ value: search, onChange: setSearch, placeholder: 'Search name or email' }}
        filters={[
          {
            id: 'type',
            label: 'Type',
            value: type,
            onChange: (value) => setType(value as TypeFilter),
            options: ['Client', 'One Up Webmaster', 'All'].map((value) => ({ value, label: value })),
          },
        ]}
        activeFilters={activeFilters}
        primaryAction={{ label: 'Create Contact', icon: Plus, onClick: () => openCreateDrawer({ type: 'contact' }) }}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {viewMode === 'cards' ? (
      <ManagementCardGrid>
        {filtered.length === 0 && (<Card className="px-3 py-8 text-center text-sm text-muted-foreground">No contacts match this view.</Card>)}
        {filtered.map((user) => { const firstClientId = user.clientIds?.[0]; const firstClient = firstClientId ? clientMap.get(firstClientId) : undefined; return (
          <Card key={user.id} onClick={() => navigate(`/users/${user.id}`)} className={cn('cursor-pointer space-y-2 px-3 py-3', userId === user.id && 'bg-primary/5')}>
            <div className="flex items-center gap-2"><Avatar name={user.fullName} imageUrl={user.profileImageUrl} /><div className="min-w-0"><div className="truncate font-semibold">{user.fullName}</div><div className="truncate text-xs text-muted-foreground">{user.email ?? '-'}</div></div></div>
            <div className="flex flex-wrap gap-1">{(user.userTypes ?? []).map((value) => (<Badge key={value} variant="outline">{value}</Badge>))}</div>
            <div className="flex items-center justify-between text-xs text-muted-foreground"><span>{formatRelativeDate(user.lastSeenAt)}</span>{firstClientId && <button type="button" onClick={(event) => {event.stopPropagation(); setNestedClientId(firstClientId);}} className="rounded-full border border-border px-2 py-1">{firstClient?.name ?? firstClientId}</button>}</div>
          </Card>
        );})}
      </ManagementCardGrid>
      ) : (

      <ManagementTable>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13px]">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-[11px] font-semibold uppercase tracking-[0.5px] text-muted-foreground">
                <th className="px-3 py-2.5">Contact</th>
                <th className="px-3 py-2.5">Email</th>
                <th className="px-3 py-2.5">Type</th>
                <th className="px-3 py-2.5">Client</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5">Last Seen</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-12 text-center text-sm text-muted-foreground">
                    No contacts match this view.
                  </td>
                </tr>
              )}
              {filtered.map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  active={userId === user.id}
                  clientMap={clientMap}
                  onOpen={() => navigate(`/users/${user.id}`)}
                  onOpenClient={setNestedClientId}
                />
              ))}
            </tbody>
          </table>
        </div>
      </ManagementTable>
      )}

      <ManagementFooterSummary>
        Showing <span className="tabular-nums text-foreground">{filtered.length}</span>
        {users.length !== filtered.length && (
          <>
            {' '}of <span className="tabular-nums">{users.length}</span> loaded
          </>
        )}
      </ManagementFooterSummary>

      {userId && (
        <UserDrawer
          userId={userId}
          onClose={() => navigate('/users')}
          onOpenTask={setNestedTaskId}
          onOpenClient={setNestedClientId}
        />
      )}
      {nestedTaskId && <TaskDrawer taskId={nestedTaskId} onClose={() => setNestedTaskId(undefined)} />}
      {nestedClientId && (
        <ClientDrawer
          clientId={nestedClientId}
          onClose={() => setNestedClientId(undefined)}
          onOpenTask={setNestedTaskId}
          onOpenUser={(id) => navigate(`/users/${id}`)}
        />
      )}
    </ManagementPage>
  );
}

function UserRow({
  user,
  active,
  clientMap,
  onOpen,
  onOpenClient,
}: {
  user: User;
  active: boolean;
  clientMap: Map<string, Client>;
  onOpen: () => void;
  onOpenClient: (id: string) => void;
}) {
  const firstClientId = user.clientIds?.[0];
  const firstClient = firstClientId ? clientMap.get(firstClientId) : undefined;

  return (
    <tr
      onClick={onOpen}
      tabIndex={0}
      role="button"
      className={cn(
        'cursor-pointer border-t border-border transition-colors hover:bg-muted/30 focus-visible:bg-muted/40 focus-visible:outline-none',
        active && 'bg-primary/5',
      )}
    >
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2">
          <Avatar name={user.fullName} imageUrl={user.profileImageUrl} />
          <div>
            <div className="font-semibold text-foreground">{user.fullName}</div>
            {user.title && <div className="text-[11px] text-muted-foreground">{user.title}</div>}
          </div>
        </div>
      </td>
      <td className="px-3 py-2.5 text-muted-foreground">{user.email ?? '-'}</td>
      <td className="px-3 py-2.5">
        <div className="flex flex-wrap gap-1">
          {(user.userTypes ?? []).map((value) => (
            <Badge key={value} variant="outline">
              {value}
            </Badge>
          ))}
          {(user.userTypes?.length ?? 0) === 0 && <MutedDash />}
        </div>
      </td>
      <td className="px-3 py-2.5">
        {firstClientId ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onOpenClient(firstClientId);
            }}
            className="rounded-full border border-border bg-card px-2 py-0.5 text-[11px] font-medium text-foreground hover:border-primary/40 hover:text-primary"
          >
            {firstClient?.name ?? firstClientId}
          </button>
        ) : (
          <MutedDash />
        )}
      </td>
      <td className="px-3 py-2.5">
        {user.status ? <Badge variant="muted">{user.status}</Badge> : <MutedDash />}
      </td>
      <td className="px-3 py-2.5 text-muted-foreground">
        {formatRelativeDate(user.lastSeenAt)}
      </td>
    </tr>
  );
}
